// CTA server functions — analytics logging, token resolution, and
// onboarding-step routing. Server-side state means the redirect logic
// keeps working even if the browser strips query parameters during the
// auth handoff.
import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader, setCookie, getCookie } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const CTA_COOKIE_NAME = "lr_cta_ctx";
const CTA_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days

// ---------- shared shape ----------

const ctaContextSchema = z.object({
  token: z.string().max(64).optional(),
  campaign: z.string().max(64).optional(),
  shift_id: z.string().uuid().optional(),
  postcode: z.string().max(16).optional(),
  radius_km: z.number().int().min(1).max(200).optional(),
  ref: z.string().max(64).optional(),
});

export type CtaContext = z.infer<typeof ctaContextSchema>;

// ---------- log a redirect event ----------

const logEventSchema = z.object({
  stage: z.enum(["auth", "dashboard", "onboarding"]),
  from_path: z.string().max(256),
  to_path: z.string().max(256),
  reason: z.string().max(120).nullish(),
  role: z.string().max(32).nullish(),
  onboarding_status: z.string().max(32).nullish(),
  token: z.string().max(64).nullish(),
  campaign: z.string().max(64).nullish(),
  shift_id: z.string().uuid().nullish(),
  postcode: z.string().max(16).nullish(),
  radius_km: z.number().int().min(1).max(200).nullish(),
});

export const logCtaEvent = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) => logEventSchema.parse(d))
  .handler(async ({ data }) => {
    try {
      const userAgent = getRequestHeader("user-agent") ?? null;
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

      // Attribution is only ever derived server-side. We look up the
      // authenticated user (if any) via the request bearer token — never
      // trust a client-supplied user_id. Unauthenticated callers (landing
      // pages, /auth) log with user_id = null; the actual user↔token bind
      // happens later in `consumeCtaToken`, which is auth-gated.
      let resolvedUserId: string | null = null;
      try {
        const authHeader = getRequestHeader("authorization");
        if (authHeader?.startsWith("Bearer ")) {
          const token = authHeader.slice(7);
          const { data: claimData } = await supabaseAdmin.auth.getClaims(token);
          const sub = claimData?.claims?.sub;
          if (typeof sub === "string") resolvedUserId = sub;
        }
      } catch { /* ignore — analytics never blocks the redirect */ }

      // If a CTA token was supplied, only accept the campaign / shift_id that
      // the token actually maps to. This prevents anonymous callers from
      // fabricating attribution rows for arbitrary campaigns or shifts.
      let campaign = data.campaign ?? null;
      let shiftId = data.shift_id ?? null;
      let tokenValue: string | null = null;
      if (data.token) {
        const { data: tokRow } = await supabaseAdmin
          .from("cta_email_tokens")
          .select("token, campaign, shift_id")
          .eq("token", data.token)
          .maybeSingle();
        if (tokRow) {
          tokenValue = tokRow.token as string;
          campaign = (tokRow.campaign as string | null) ?? campaign;
          shiftId = (tokRow.shift_id as string | null) ?? shiftId;
        } else {
          // Unknown token → drop it and its campaign/shift claims entirely.
          campaign = null;
          shiftId = null;
        }
      }

      await supabaseAdmin.from("cta_events").insert({
        stage: data.stage,
        from_path: data.from_path,
        to_path: data.to_path,
        reason: data.reason ?? null,
        role: data.role ?? null,
        onboarding_status: data.onboarding_status ?? null,
        token: tokenValue,
        campaign,
        shift_id: shiftId,
        postcode: data.postcode ?? null,
        radius_km: data.radius_km ?? null,
        user_id: resolvedUserId,
        user_agent: userAgent,
      });
    } catch (err) {
      // Never let analytics break a redirect.
      console.error("[cta] logCtaEvent failed", err);
    }
    return { ok: true as const };
  });

// ---------- resolve a token: look up DB row, set signed cookie ----------

export const resolveCtaToken = createServerFn({ method: "POST" })
  .inputValidator((d: unknown) =>
    z.object({ token: z.string().min(8).max(64) }).parse(d),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { signCookieValue } = await import("./cta.server");

    const { data: row, error } = await supabaseAdmin
      .from("cta_email_tokens")
      .select("token, campaign, shift_id, postcode, radius_km, recipient_email, recipient_user_id, expires_at, consumed_at")
      .eq("token", data.token)
      .maybeSingle();

    const logInvalid = async (reason: "invalid_token" | "expired_token") => {
      try {
        await supabaseAdmin.from("cta_events").insert({
          stage: "auth", from_path: "/auth", to_path: "/auth",
          reason, token: data.token.slice(0, 12),
        });
      } catch { /* never let logging break the redirect */ }
    };

    if (error || !row) {
      await logInvalid("invalid_token");
      return { ok: false as const, reason: "invalid_token" as const };
    }
    const now = Date.now();
    if (row.expires_at && new Date(row.expires_at).getTime() < now) {
      await logInvalid("expired_token");
      return { ok: false as const, reason: "expired_token" as const };
    }

    const ctx: CtaContext = {
      token: row.token,
      campaign: row.campaign,
      shift_id: row.shift_id ?? undefined,
      postcode: row.postcode ?? undefined,
      radius_km: row.radius_km ?? undefined,
      ref: "email",
    };
    // Cookie lifetime mirrors token expiry — never longer than the token.
    const cookieTtlSec = Math.max(
      60,
      Math.min(
        CTA_COOKIE_MAX_AGE,
        Math.floor((new Date(row.expires_at).getTime() - now) / 1000),
      ),
    );
    setCookie(
      CTA_COOKIE_NAME,
      signCookieValue({ ...ctx, exp: Math.floor(now / 1000) + cookieTtlSec }),
      { httpOnly: true, sameSite: "lax", secure: true, path: "/", maxAge: cookieTtlSec },
    );
    return { ok: true as const, context: ctx };
  });

// ---------- read current CTA context from cookie ----------

export const getCtaContext = createServerFn({ method: "GET" }).handler(async () => {
  const { verifyCookieValue } = await import("./cta.server");
  const raw = getCookie(CTA_COOKIE_NAME);
  const ctx = verifyCookieValue<CtaContext & { exp?: number }>(raw ?? null);
  if (!ctx) return { context: null };
  if (typeof ctx.exp === "number" && ctx.exp * 1000 < Date.now()) {
    // Expired cookie — clear it and behave as if there's no context.
    setCookie(CTA_COOKIE_NAME, "", { path: "/", maxAge: 0 });
    return { context: null };
  }
  const { exp: _exp, ...rest } = ctx;
  return { context: rest as CtaContext };
});

// ---------- consume token: attach signup attribution ----------

export const consumeCtaToken = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ token: z.string().min(8).max(64) }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin
      .from("cta_email_tokens")
      .update({ consumed_at: new Date().toISOString(), consumed_by_user_id: context.userId })
      .eq("token", data.token)
      .is("consumed_at", null);
    return { ok: true as const };
  });

// ---------- safe shift preview for prefill ----------

export const getCtaShiftPreview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z.object({ shift_id: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    // Definer function only returns OPEN, future shifts and projects only
    // non-PII fields — bartenders cannot enumerate other venues' shifts.
    const { data: rows, error } = await context.supabase.rpc("get_cta_shift_preview", {
      _shift_id: data.shift_id,
    } as never);
    if (error) return { ok: false as const, reason: error.message, shift: null };
    const rawRows = (rows ?? []) as Array<{
      shift_id: string;
      role_type: string;
      start_time: string;
      end_time: string;
      address: string | null;
      postcode: string | null;
      hourly_rate: number;
    }>;
    const row = rawRows[0] ?? null;
    return { ok: true as const, reason: null as string | null, shift: row };
  });

// ---------- single source of truth for "where does this user belong?" ----------

type Destination = {
  to: string;
  reason: string;
  role: string;
  onboarding_status: string | null;
};

export const getOnboardingDestination = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<Destination> => {
    const { supabase, userId } = context;
    const { data: isAdmin } = await supabase.rpc("is_current_user_admin");
    if (isAdmin === true) {
      return { to: "/admin", reason: "role:admin", role: "admin", onboarding_status: null };
    }
    const { data: prof } = await supabase
      .from("profiles")
      .select("role, onboarding_status, venue_onboarding_completed_at, bartender_onboarding_completed_at")
      .eq("id", userId)
      .maybeSingle();
    const role = (prof?.role as string | undefined) ?? "bartender";
    const status = (prof?.onboarding_status as string | null | undefined) ?? null;
    const venueOnboardingDone = Boolean(
      (prof as { venue_onboarding_completed_at?: string | null } | null)
        ?.venue_onboarding_completed_at,
    );
    const bartenderOnboardingDone = Boolean(
      (prof as { bartender_onboarding_completed_at?: string | null } | null)
        ?.bartender_onboarding_completed_at,
    );

    if (role === "venue") {
      if (status === "approved") {
        return { to: "/venue", reason: "role:venue,onboarding:approved", role, onboarding_status: status };
      }
      if (!venueOnboardingDone) {
        return { to: "/venue/onboarding", reason: `role:venue,onboarding:${status ?? "unknown"},wizard:incomplete`, role, onboarding_status: status };
      }
      return { to: "/venue-pending", reason: `role:venue,onboarding:${status ?? "unknown"}`, role, onboarding_status: status };
    }

    // Bartender — wizard always runs before the assessment.
    if (!bartenderOnboardingDone && status !== "rejected" && status !== "approved" && status !== "pending_cv_review") {
      return { to: "/bartender/onboarding", reason: `role:bartender,wizard:incomplete`, role, onboarding_status: status };
    }
    switch (status) {
      case "rejected":
        return { to: "/bartender/rejected", reason: "role:bartender,onboarding:rejected", role, onboarding_status: status };
      case "pending_quiz":
        return { to: "/bartender/application", reason: "role:bartender,onboarding:pending_quiz", role, onboarding_status: status };
      case "pending_cv_review":
        return { to: "/bartender/application", reason: "role:bartender,onboarding:pending_cv_review", role, onboarding_status: status };
      case "approved":
      default:
        return { to: "/bartender", reason: `role:bartender,onboarding:${status ?? "unknown"}`, role, onboarding_status: status };
    }
  });

// ---------- admin: CTA attribution dashboard ----------

async function assertAdmin(ctx: { supabase: { rpc: (n: string) => Promise<{ data: unknown }> }; userId: string }) {
  const { data } = await ctx.supabase.rpc("is_current_user_admin");
  if (data !== true) throw new Error("Forbidden");
}

export const getCtaAttribution = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertAdmin(context as never);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const [summaryRes, recentRes, tokensRes] = await Promise.all([
      supabaseAdmin
        .from("cta_attribution_summary")
        .select("*")
        .order("event_count", { ascending: false })
        .limit(200),
      supabaseAdmin
        .from("cta_events")
        .select("created_at, stage, campaign, shift_id, role, onboarding_status, to_path, reason, user_id, token")
        .order("created_at", { ascending: false })
        .limit(100),
      supabaseAdmin
        .from("cta_email_tokens")
        .select("token, campaign, shift_id, recipient_email, created_at, expires_at, consumed_at, consumed_by_user_id")
        .order("created_at", { ascending: false })
        .limit(100),
    ]);

    return {
      summary: summaryRes.data ?? [],
      recent: recentRes.data ?? [],
      tokens: tokensRes.data ?? [],
    };
  });
